import { ApolloClient, gql } from '@apollo/client';

// Dedupes concurrent lookups for the same partner within a single request
// (page.tsx, layout.tsx and generateMetadata all call this for the same
// partner). Entries are removed as soon as they settle, so this never holds
// data across requests — freshness is controlled entirely by the fetch's
// `next.revalidate` option below.
const pendingPartnerFetches = new Map<string, Promise<any>>();

const REVALIDATE_SECONDS = 300;

export async function getB2bPartnerByName(
  partnerName: string,
  strapiUrl: string,
  strapiToken: string,
  enrollmentClient: ApolloClient<any>
): Promise<Partner | undefined> {
  const key = partnerName.toLowerCase();
  const url = `${strapiUrl}/api/partners?populate=*&filters[name][$eqi]=${partnerName}`;

  if (!pendingPartnerFetches.has(key)) {
    // `next.revalidate` is only meaningful when this runs inside Next.js
    // (b2b's SSR); other consumers (e.g. the plan-finder-z1 SPA) just ignore
    // it. Typed as an intersection instead of relying on Next's ambient
    // `RequestInit` augmentation, which isn't visible in non-Next apps.
    const fetchInit: RequestInit & { next?: { revalidate: number } } = {
      headers: {
        Authorization: 'Bearer ' + strapiToken,
      },
      next: { revalidate: REVALIDATE_SECONDS },
    };
    const promise = fetch(url, fetchInit)
        .then((res) => res.json())
        // Strapi errors come back as { data: null, error } — treat as "no partner"
        .then((res) => res?.data?.[0])
        .then(async (res) => {
          if (res) {
            const item = res.attributes;

            const agentId = item?.agent?.data?.attributes.admin_id || null;
            let b2bMeetingUrl: string | undefined = undefined;

            if (item?.calendlyLinkOverride) {
              b2bMeetingUrl = item?.calendlyLinkOverride;
            } else if (agentId) {
              b2bMeetingUrl = await getAgentCalendlyUrl(
                agentId,
                enrollmentClient
              );
            }

            return {
              name: item?.name || '',
              logo: `${strapiUrl}${item?.logo?.data?.attributes.url}`,
              logoHeight: item?.logoHeight || 60,
              calendlyLinkOverride: item?.calendlyLinkOverride || '',
              color: item?.color || '',
              phoneNumber: item?.phoneNumber ?? '8889697667',
              metaImage: {
                url: item?.metaImage?.data?.attributes.url
                  ? `${strapiUrl}${item?.metaImage?.data?.attributes.url}`
                  : '',
                height: item?.metaImage?.data?.attributes.height || null,
                width: item?.metaImage?.data?.attributes.width || null,
              },
              agent: {
                name: item?.agent?.data?.attributes.name || null,
                id: agentId,
                b2bMeetingUrl:
                  b2bMeetingUrl ||
                  'https://calendly.com/coverright-advisors/medicare-consultation-partners',
              },
            };
          }
          return undefined;
        });

    pendingPartnerFetches.set(
      key,
      promise.finally(() => pendingPartnerFetches.delete(key))
    );
  }
  return pendingPartnerFetches.get(key);
}

async function getAgentCalendlyUrl(
  id: string,
  enrollmentClient: ApolloClient<any>
): Promise<string | undefined> {
  return await enrollmentClient
    .query({
      query: gql`
    query agentById($id: UUID!) {
  agentById(id: $id) {
    b2bMeetingUrl
    email
    firstName
    id
    lastName
    picUrl
    phone
    enrollmentMeetingUrl
    planReviewUrl
    consultationUrl
  }
}
    `,
      variables: { id },
    })
    .then((data) => data.data?.agentById.b2bMeetingUrl || undefined);
}

export interface Partner {
  name: string;
  logo?: string;
  color?: string;
  phoneNumber: string;
  calendlyLinkOverride?: string;
  metaImage?: { width: number; height: number; url: string };
  agent: PartnerAgent;
  logoHeight: number;
  addressLines?: string[];
  displayName?: string;
}

export interface PartnerAgent {
  b2bMeetingUrl?: string | null;
  email: string;
  firstName: string;
  id: any;
  lastName: string;
  picUrl?: string | null;
  phone?: string | null;
  enrollmentMeetingUrl?: string | null;
  planReviewUrl?: string | null;
  consultationUrl?: string | null;
}
